export type StatusTone =
  | 'neutral'
  | 'pending'
  | 'progress'
  | 'success'
  | 'warning'
  | 'danger'
  | 'halt'
  | 'info'
  | 'accent'

export const STATUS_TONE_CLASS: Record<StatusTone, string> = {
  neutral: 'bg-status-neutral text-status-neutral-foreground',
  pending: 'bg-status-pending text-status-pending-foreground',
  progress: 'bg-status-progress text-status-progress-foreground',
  success: 'bg-status-success text-status-success-foreground',
  warning: 'bg-status-warning text-status-warning-foreground',
  danger: 'bg-status-danger text-status-danger-foreground',
  halt: 'bg-status-halt text-status-halt-foreground',
  info: 'bg-status-info text-status-info-foreground',
  accent: 'bg-status-accent text-status-accent-foreground',
}

export const STATUS_TONE_MEANING: Record<StatusTone, string> = {
  neutral: 'Nothing to act on — not started, not applicable, or unrecognised',
  pending: 'Waiting on someone to act',
  progress: 'Underway — started but not finished',
  success: 'Finished successfully',
  warning: 'Deadline approaching',
  danger: 'Failed, refused, or lapsed',
  halt: 'Stopped or removed — no longer in play',
  info: 'Did not take place',
  accent: 'Settled financially',
}

export type StatusStateKey =
  | 'draft'
  | 'not_required'
  | 'unknown'
  | 'pending'
  | 'final'
  | 'partially_signed'
  | 'invoiced'
  | 'approved'
  | 'completed'
  | 'executed'
  | 'fully_signed'
  | 'concluded'
  | 'paid'
  | 'cancelled'
  | 'rejected'
  | 'expired'
  | 'no_show'
  | 'halt'
  | 'deleted'

export interface StatusDescriptor {
  tone: StatusTone
  label: string
  glyph: string
}

export const STATUS_VOCABULARY: Record<StatusStateKey, StatusDescriptor> = {
  draft: {
    tone: 'neutral',
    label: 'Draft',
    glyph: '<path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/>',
  },
  not_required: {
    tone: 'neutral',
    label: 'Not Required',
    glyph: '<circle cx="12" cy="12" r="10"/><path d="M8 12h8"/>',
  },
  unknown: {
    tone: 'neutral',
    label: 'Unknown',
    glyph:
      '<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/>',
  },
  pending: {
    tone: 'pending',
    label: 'Pending',
    glyph: '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
  },
  final: {
    tone: 'progress',
    label: 'Final',
    glyph:
      '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v5h5"/><path d="m9 15 2 2 4-4"/>',
  },
  partially_signed: {
    tone: 'progress',
    label: 'Partially Signed',
    glyph:
      '<path d="m12 19 7-7 3 3-7 7-3-3z"/><path d="m18 13-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="m2 2 7.586 7.586"/><circle cx="11" cy="11" r="2"/>',
  },
  invoiced: {
    tone: 'progress',
    label: 'Invoiced',
    glyph:
      '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v5h5"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/>',
  },
  approved: {
    tone: 'success',
    label: 'Approved',
    glyph:
      '<path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/>',
  },
  completed: {
    tone: 'success',
    label: 'Completed',
    glyph: '<circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/>',
  },
  executed: {
    tone: 'success',
    label: 'Executed',
    glyph: '<path d="M18 6 7 17l-5-5"/><path d="m22 10-7.5 7.5L13 16"/>',
  },
  fully_signed: {
    tone: 'success',
    label: 'Fully Signed',
    glyph:
      '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/>',
  },
  concluded: {
    tone: 'success',
    label: 'Concluded',
    glyph:
      '<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" x2="4" y1="22" y2="15"/>',
  },
  paid: {
    tone: 'accent',
    label: 'Paid',
    glyph:
      '<rect width="20" height="12" x="2" y="6" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01"/><path d="M18 12h.01"/>',
  },
  cancelled: {
    tone: 'danger',
    label: 'Cancelled',
    glyph: '<circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/>',
  },
  rejected: {
    tone: 'danger',
    label: 'Rejected',
    glyph:
      '<path d="M17 14V2"/><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z"/>',
  },
  expired: {
    tone: 'danger',
    label: 'Expired',
    glyph:
      '<path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/><path d="m14 14-4 4"/><path d="m10 14 4 4"/>',
  },
  no_show: {
    tone: 'info',
    label: 'No Show',
    glyph:
      '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="17" x2="22" y1="8" y2="13"/><line x1="22" x2="17" y1="8" y2="13"/>',
  },
  halt: {
    tone: 'halt',
    label: 'Halt',
    glyph:
      '<rect x="14" y="4" width="4" height="16" rx="1"/><rect x="6" y="4" width="4" height="16" rx="1"/>',
  },
  deleted: {
    tone: 'halt',
    label: 'Deleted',
    glyph:
      '<path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/>',
  },
}

export const resolveStatus = (state: string | null | undefined): StatusDescriptor =>
  STATUS_VOCABULARY[state as StatusStateKey] ?? STATUS_VOCABULARY.unknown

export const isStatusStateKey = (state: string): state is StatusStateKey =>
  Object.prototype.hasOwnProperty.call(STATUS_VOCABULARY, state)

export const STATUS_PILL_BASE =
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium leading-none align-middle'

export const statusPillClass = (tone: StatusTone): string =>
  `${STATUS_PILL_BASE} ${STATUS_TONE_CLASS[tone]}`

export const CATEGORY_CHIP_CLASS =
  'inline-flex items-center rounded-md bg-secondary-surface px-2 py-1 text-xs font-medium leading-none align-middle text-foreground'

export type DeadlineTone = 'on_time' | 'due_soon' | 'overdue' | 'closed' | 'completed'

export interface DeadlineDescriptor {
  tone: DeadlineTone
  label: string
  glyph: string
}

export const DEADLINE_TONE_CLASS: Record<DeadlineTone, string> = {
  on_time: 'text-status-success dark:text-status-success-foreground',
  due_soon: 'text-status-warning dark:text-status-warning-foreground',
  overdue: 'text-status-danger dark:text-status-danger-foreground',
  closed: 'text-muted-foreground',
  completed: 'text-status-success dark:text-status-success-foreground',
}

export const DEADLINE_BASE =
  'inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium leading-none align-middle'

export const DEADLINE_VOCABULARY: Record<DeadlineTone, DeadlineDescriptor> = {
  on_time: { tone: 'on_time', label: 'On Time', glyph: '<path d="M20 6 9 17l-5-5"/>' },
  due_soon: {
    tone: 'due_soon',
    label: 'Due Soon',
    glyph:
      '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
  },
  overdue: {
    tone: 'overdue',
    label: 'Overdue',
    glyph: '<circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/>',
  },
  closed: { tone: 'closed', label: '—', glyph: '<path d="M5 12h14"/>' },
  completed: { tone: 'completed', label: 'Completed', glyph: '<path d="M20 6 9 17l-5-5"/>' },
}

export const deadlineClass = (tone: DeadlineTone): string =>
  `${DEADLINE_BASE} ${DEADLINE_TONE_CLASS[tone]}`

export const TERMINAL_STATUS_KEYS: readonly StatusStateKey[] = [
  'completed',
  'executed',
  'invoiced',
  'paid',
  'cancelled',
  'deleted',
  'halt',
  'no_show',
  'concluded',
]

export const isTerminalStatus = (state: string | null | undefined): boolean =>
  !!state && TERMINAL_STATUS_KEYS.includes(state as StatusStateKey)

// Terminal statuses that represent a task that was successfully carried out — its
// Progress reads "Completed" (green check) rather than the neutral "—" the other
// terminal statuses (cancelled / deleted / halt / no_show) get.
export const COMPLETED_STATUS_KEYS: readonly StatusStateKey[] = [
  'completed',
  'executed',
  'invoiced',
  'paid',
  'concluded',
]

export const isCompletedStatus = (state: string | null | undefined): boolean =>
  !!state && COMPLETED_STATUS_KEYS.includes(state as StatusStateKey)

export const resolveDeadline = (
  daysUntilDeadline: number,
  status?: string | null,
): DeadlineDescriptor => {
  if (isCompletedStatus(status)) return DEADLINE_VOCABULARY.completed
  if (isTerminalStatus(status)) return DEADLINE_VOCABULARY.closed
  if (daysUntilDeadline > 5) return DEADLINE_VOCABULARY.on_time
  if (daysUntilDeadline > 0) return DEADLINE_VOCABULARY.due_soon
  return DEADLINE_VOCABULARY.overdue
}

export const glyphSvg = (glyph: string, sizeClass = 'h-3.5 w-3.5 shrink-0'): string =>
  `<svg xmlns="http://www.w3.org/2000/svg" class="${sizeClass}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${glyph}</svg>`
